import { Save, Loader2, GripVertical, Trash2 } from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";

export const inputCls =
  "w-full border border-line bg-base px-4 py-3 text-sm text-txt outline-none transition-colors duration-300 focus:border-myrtle";

export const Field = ({ label, hint, children }) => (
  <div>
    <span className="text-xs uppercase tracking-[0.15em] text-mut">{label}</span>
    <div className="mt-2">{children}</div>
    {hint && <p className="mt-1.5 text-xs leading-relaxed text-faint">{hint}</p>}
  </div>
);

export const Toggle = ({ checked, onChange, testid, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testid}
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${checked ? "bg-myrtle" : "bg-line"}`}
  >
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform duration-300 ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`} />
  </button>
);

export const SaveBar = ({ onSave, saving, lastSaved, testid }) => (
  <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-line pt-6">
    <button
      onClick={onSave}
      disabled={saving}
      data-testid={testid}
      className="flex items-center gap-2 bg-myrtle px-7 py-3 text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-myrtle-light disabled:opacity-60"
    >
      {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
    </button>
    {lastSaved && (
      <p className="text-xs text-faint" data-testid={`${testid}-last-saved`}>
        Last saved at {new Date(lastSaved).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
      </p>
    )}
  </div>
);

export const TabCard = ({ title, sub, testid, children }) => (
  <section className="border border-line bg-surface p-6 md:p-8" data-testid={testid}>
    <p className="font-batangas text-2xl tracking-tight">{title}</p>
    {sub && <p className="mt-1 text-xs uppercase tracking-[0.2em] text-faint">{sub}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

export const SortableList = ({ items, onReorder, children, testid }) => (
  <Reorder.Group axis="y" values={items} onReorder={onReorder} className="space-y-4" data-testid={testid}>
    {items.map((item, i) => children(item, i))}
  </Reorder.Group>
);

export const SortableItem = ({ item, testid, children }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item value={item} dragListener={false} dragControls={controls} className="relative border border-line bg-base p-4" data-testid={testid}>
      <button
        type="button"
        onPointerDown={(e) => controls.start(e)}
        data-testid={`${testid}-drag`}
        aria-label="Drag to reorder"
        className="absolute left-3 top-4 cursor-grab touch-none text-faint transition-colors duration-200 hover:text-pop active:cursor-grabbing"
      >
        <GripVertical size={16} />
      </button>
      <div className="pl-8">{children}</div>
    </Reorder.Item>
  );
};

export const DeleteButton = ({ onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-label="Delete"
    className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-faint transition-colors duration-200 hover:text-red-400"
  >
    <Trash2 size={14} /> Delete
  </button>
);
